<template>
	<div
		class="MultiselectProxyInput"
		:class="{ 'isDisabled': props.disabled }"
	>
		<button
			type="button"
			class="ProxyInputButton"
			:disabled="props.disabled"
			:aria-label="props.placeholder"
			@click="handleClick"
		>
			<div class="ProxyInputContent">
				<template v-if="hasValue">
					<div v-if="isSingleMode" class="SingleValue">
						<i v-if="selectedOption?.icon" :class="`Icon ${selectedOption.icon}`" aria-hidden="true"></i>
						<span class="ValueText">{{ selectedOption?.label }}</span>
					</div>
					<div v-else class="TagsValue">
						<Chip
							v-for="item in selectedOptions"
							:key="item.value"
							:label="item.label"
							:count="item.count"
							@remove="handleRemoveItem(item.value)"
						/>
					</div>
				</template>
				<span v-else class="Placeholder">{{ props.placeholder }}</span>
			</div>

			<div class="ProxyInputActions">
				<i
					v-if="props.canClear && hasValue"
					class="ClearIcon lnr-cross"
					aria-label="Clear selection"
					role="button"
					tabindex="0"
					@click.stop="handleClear"
					@keydown.enter.stop="handleClear"
					@keydown.space.stop="handleClear"
				></i>
				<i class="CaretIcon lnr-chevron-down" aria-hidden="true"></i>
			</div>
		</button>

		<p v-if="props.description" class="ProxyDescription">{{ props.description }}</p>
		<p v-if="props.warning" class="ProxyWarning">{{ props.warning }}</p>
	</div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import Chip from '@components/TagsElement/Chip.vue';
import { multiselectPropsDefinition } from '@app/interfaces/useMultiselect.interface';
import { MultiselectMode } from '@app/enums/MultiselectMode';
import { IMultiselectOption } from '@app/interfaces/dtos/IMultiselectOption';

const props = defineProps(multiselectPropsDefinition);

const emit = defineEmits<{
	'click': [];
	'clear': [];
	'removeItem': [value: string | number];
}>();

const isSingleMode = computed(() => props.mode === MultiselectMode.SINGLE);

const hasValue = computed(() => {
	if (props.modelValue === null || props.modelValue === undefined) return false;
	if (Array.isArray(props.modelValue)) return props.modelValue.length > 0;
	return true;
});

const selectedOption = computed(() => {
	if (!hasValue.value || !isSingleMode.value) return null;
	return props.options!.find((opt: IMultiselectOption) => opt.value === props.modelValue);
});

const selectedOptions = computed(() => {
	if (!hasValue.value || isSingleMode.value) return [];
	const values = Array.isArray(props.modelValue) ? props.modelValue : [props.modelValue];
	return props.options!.filter((opt: IMultiselectOption) => values.includes(opt.value));
});

const handleClick = (): void => {
	if (props.disabled) return;
	emit('click');
};

const handleClear = (): void => {
	if (props.disabled) return;
	emit('clear');
};

const handleRemoveItem = (value: string | number): void => {
	if (props.disabled) return;
	emit('removeItem', value);
};
</script>

<style scoped lang="scss">
.MultiselectProxyInput {
	width: 100%;

	&.isDisabled {
		opacity: 0.5;
		pointer-events: none;
	}

	.ProxyInputButton {
		display: flex;
		align-items: center;
		justify-content: space-between;
		width: 100%;
		min-height: 2.5rem;
		background: $White;
		border: 1px solid $Grey-600;
		border-radius: 0.5rem;
		cursor: pointer;
		text-align: left;
		@include PaddingAreas(1, 0.5);

		&:hover:not(:disabled) {
			background: $Blue-100;
		}

		&:focus-visible {
			outline: 2px solid $Blue-600;
			outline-offset: -2px;
		}

		&:disabled {
			cursor: not-allowed;
			background: $Grey-100;
		}
	}

	.ProxyInputContent {
		flex: 1;
		min-width: 0;
		display: flex;
		align-items: center;
	}

	.Placeholder {
		color: $Grey-600;
		@include TextStyle(UiLargeRegular);
	}

	.SingleValue {
		display: flex;
		align-items: center;
		gap: 0.5rem;
		min-width: 0;

		.Icon {
			width: 1rem;
			height: 1rem;
			flex-shrink: 0;
		}

		.ValueText {
			color: $Grey-900;
			overflow: hidden;
			text-overflow: ellipsis;
			white-space: nowrap;
			@include TextStyle(UiLargeRegular);
		}
	}

	.TagsValue {
		display: flex;
		align-items: center;
		flex-wrap: wrap;
		gap: 0.5rem;
		min-width: 0;
	}

	.ProxyInputActions {
		display: flex;
		align-items: center;
		gap: 0.25rem;
		flex-shrink: 0;
		@include MarginAreas(0, 0, 0, 0.5);
	}

	.ClearIcon {
		color: $Grey-600;
		cursor: pointer;
		@include PaddingAreas(0.25);

		&:hover,
		&:focus-visible {
			color: $Grey-900;
		}
	}

	.CaretIcon {
		color: $Grey-600;
	}

	.ProxyDescription {
		color: $Grey-700;
		@include TextStyle(BodyMediumRegular);
		@include MarginAreas(0.5, 0, 0, 0);
	}

	.ProxyWarning {
		color: $Red-500;
		@include TextStyle(BodyMediumRegular);
		@include MarginAreas(0.5, 0, 0, 0);
	}
}
</style>
